import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {
  AppBar,
  Avatar,
  Box,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { Link as RouterLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useAuth } from '@/auth/AuthProvider';
import { useToast } from '@/components/Toast';
import { useAppVersion, useVersionInfo } from '@/components/VersionCheck';
import { errorMessage } from '@/api/client';
import { initials } from '@/utils/format';
import { ASSISTANT_WIDTH, AssistantPanel, AssistantProvider, useAssistant } from '@/features/assistant/AssistantPanel';
import { NAV_SECTIONS, NavItem } from './navigation';

const DRAWER_WIDTH = 264;

function useVisible() {
  const { canAny, canOthers } = useAuth();
  return (item: { anyOf?: string[]; others?: boolean }) => {
    if (!item.anyOf?.length) return true;
    if (!canAny(...item.anyOf)) return false;
    return !item.others || item.anyOf.some((c) => canOthers(c));
  };
}

function NavEntry({ item, onNavigate }: { item: NavItem; onNavigate: () => void }) {
  const { pathname } = useLocation();
  const visible = useVisible();
  const children = item.children?.filter(visible) ?? [];
  const active = pathname === item.to || pathname.startsWith(`${item.to}/`);
  const [open, setOpen] = useState(active);
  if (children.length) {
    return (
      <>
        <ListItemButton onClick={() => setOpen((o) => !o)} selected={active && !open} sx={{ borderRadius: 1, mx: 1 }}>
          <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
          <ListItemText primary={item.label} slotProps={{ primary: { variant: 'body2', fontWeight: 500 } }} />
          {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </ListItemButton>
        <Collapse in={open} unmountOnExit>
          <List disablePadding>
            {children.map((c) => (
              <ListItemButton key={c.to} component={RouterLink} to={c.to} onClick={onNavigate} selected={pathname === c.to} sx={{ pl: 7, borderRadius: 1, mx: 1 }}>
                <ListItemText primary={c.label} slotProps={{ primary: { variant: 'body2' } }} />
              </ListItemButton>
            ))}
          </List>
        </Collapse>
      </>
    );
  }
  return (
    <ListItemButton component={RouterLink} to={item.to} onClick={onNavigate} selected={active} sx={{ borderRadius: 1, mx: 1 }}>
      <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
      <ListItemText primary={item.label} slotProps={{ primary: { variant: 'body2', fontWeight: 500 } }} />
    </ListItemButton>
  );
}

function VersionLabel() {
  const version = useAppVersion();
  const { data } = useVersionInfo(version);
  if (!version) return null;
  return (
    <Typography variant="caption" color="text.secondary" component="p" sx={{ px: 3, pt: 2 }}>
      Version {version}
      {data?.apiVersion && ` · API ${data.apiVersion}`}
    </Typography>
  );
}

function Sidebar({ onNavigate }: { onNavigate: () => void }) {
  const visible = useVisible();
  const { principal } = useAuth();
  return (
    <Box component="nav" aria-label="Main navigation" sx={{ overflowY: 'auto', height: '100%', pb: 2, display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ gap: 1.5 }}>
        <Box component="img" src="/favicon.svg" alt="" sx={{ width: 28, height: 28 }} />
        <Box>
          <Typography variant="h6" lineHeight={1.2}>
            AI Centric HRMS
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {principal?.tenant?.displayName}
          </Typography>
        </Box>
      </Toolbar>
      <Divider />
      {NAV_SECTIONS.map((section) => {
        const items = section.items.filter(visible);
        if (!items.length) return null;
        return (
          <List key={section.title} dense subheader={<ListSubheader sx={{ bgcolor: 'transparent', lineHeight: '32px', mt: 1 }}>{section.title}</ListSubheader>}>
            {items.map((item) => (
              <NavEntry key={item.to} item={item} onNavigate={onNavigate} />
            ))}
          </List>
        );
      })}
      <Box sx={{ mt: 'auto' }}>
        <VersionLabel />
      </Box>
    </Box>
  );
}

export function AppShell() {
  return (
    <AssistantProvider>
      <Shell />
    </AssistantProvider>
  );
}

function Shell() {
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuEl, setMenuEl] = useState<HTMLElement | null>(null);
  const { principal, logout, switchTenant } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const assistant = useAssistant();
  const panelWidth = desktop && assistant.open ? ASSISTANT_WIDTH : 0;
  const others = principal?.memberships.filter((m) => m.tenantId !== principal.tenant?.id) ?? [];

  const drawer = <Sidebar onNavigate={() => setMobileOpen(false)} />;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <AppBar position="fixed" color="inherit" sx={{ borderBottom: 1, borderColor: 'divider', zIndex: (t) => t.zIndex.drawer + 1, ml: { md: `${DRAWER_WIDTH}px` }, width: { md: `calc(100% - ${DRAWER_WIDTH + panelWidth}px)` }, mr: `${panelWidth}px` }}>
        <Toolbar sx={{ gap: 1 }}>
          {!desktop && (
            <IconButton edge="start" aria-label="Open navigation" onClick={() => setMobileOpen(true)}>
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="subtitle1" sx={{ flexGrow: 1, fontWeight: 600 }} noWrap>
            {principal?.tenant?.displayName}
          </Typography>
          <Tooltip title={assistant.open ? 'Hide assistant' : 'Ask AI assistant'}>
            <IconButton onClick={assistant.toggle} aria-label="Toggle AI assistant" aria-pressed={assistant.open} color={assistant.open ? 'primary' : 'default'}>
              <AutoAwesomeIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Account">
            <IconButton onClick={(e) => setMenuEl(e.currentTarget)} aria-label="Account menu" aria-haspopup="menu">
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14 }}>{initials(principal?.displayName ?? '?')}</Avatar>
            </IconButton>
          </Tooltip>
          <Menu anchorEl={menuEl} open={!!menuEl} onClose={() => setMenuEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle2" color="text.primary">
                {principal?.displayName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {principal?.email}
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary">
                {principal?.roles.map((r) => r.name).join(', ')}
              </Typography>
            </Box>
            <Divider />
            <MenuItem component={RouterLink} to="/self/profile" onClick={() => setMenuEl(null)}>
              My profile
            </MenuItem>
            <MenuItem component={RouterLink} to="/account/security" onClick={() => setMenuEl(null)}>
              Password & sessions
            </MenuItem>
            {others.map((m) => (
              <MenuItem
                key={m.tenantId}
                onClick={async () => {
                  setMenuEl(null);
                  try {
                    await switchTenant(m.tenantId);
                    navigate('/app/dashboard');
                  } catch (e) {
                    toast(errorMessage(e), 'error');
                  }
                }}
              >
                <ListItemIcon>
                  <SwapHorizIcon fontSize="small" />
                </ListItemIcon>
                Switch to {m.tenantName}
              </MenuItem>
            ))}
            <Divider />
            <MenuItem
              onClick={async () => {
                setMenuEl(null);
                await logout();
                navigate('/login');
              }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Sign out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box component="aside" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        {desktop ? (
          <Drawer variant="permanent" open sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box', borderRight: 1, borderColor: 'divider' } }}>
            {drawer}
          </Drawer>
        ) : (
          <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)} ModalProps={{ keepMounted: true }} sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}>
            {drawer}
          </Drawer>
        )}
      </Box>
      <Box component="main" id="main" tabIndex={-1} sx={{ flexGrow: 1, minWidth: 0, p: { xs: 2, md: 3 }, pt: { xs: 10, md: 11 }, bgcolor: 'background.default' }}>
        <Outlet />
      </Box>
      {desktop ? (
        assistant.open && (
          <Box component="aside" sx={{ width: ASSISTANT_WIDTH, flexShrink: 0, position: 'sticky', top: 0, height: '100vh', borderLeft: 1, borderColor: 'divider' }}>
            <AssistantPanel />
          </Box>
        )
      ) : (
        <Drawer anchor="right" variant="temporary" open={assistant.open} onClose={() => assistant.setOpen(false)} sx={{ '& .MuiDrawer-paper': { width: '100%', maxWidth: ASSISTANT_WIDTH } }}>
          <AssistantPanel />
        </Drawer>
      )}
    </Box>
  );
}
